import Cookies from "js-cookie";
import { onLoadPhase } from "src/vendor";
import { consentFramework, LibCategories, setConsent } from ".";
import { captureException } from "../error";

import { send } from "../fetch";
import { applyLDUFlag } from "../pixels/publishers/facebook";
import getStore from "../redux/store";

const OPT_OUT_COOKIE_NAME = "ccpaOptOut";

type OptOutStatus = {
  optedOut: boolean;
};

export async function setOptOutEmail(newEmail: string) {
  if (
    consentFramework() !== "optOut" ||
    // Don't reset if the session is opted out
    Cookies.get(OPT_OUT_COOKIE_NAME) === "true"
  ) {
    return;
  }

  Cookies.remove(OPT_OUT_COOKIE_NAME);
  setConsent([LibCategories.strictlyNecessary]);
  await ensureCookie(newEmail);
}

export async function bootstrapOptOut() {
  // Use load phase to sync with the redux store to ensure that we have
  // the complete user data before executing.
  onLoadPhase("after-context-init", async () => ensureCookie());

  applyOptOut();
}

export function optOutAllowsCookies() {
  return Cookies.get(OPT_OUT_COOKIE_NAME) === "false";
}

function applyOptOut() {
  // If not opted out, grant implicit full consent
  if (optOutAllowsCookies()) {
    setConsent([
      LibCategories.strictlyNecessary,
      LibCategories.performanceCookies,
      LibCategories.functionalCookies,
      LibCategories.targetingCookies,
      LibCategories.socialMediaCookies,
    ]);
  } else {
    setConsent([LibCategories.strictlyNecessary]);
  }
}

export async function ensureCookie(
  email = getStore().getState().userData.email
) {
  const cookie = Cookies.get(OPT_OUT_COOKIE_NAME);
  // If the cookie is not set, attempt to determine from the server
  if (!cookie && email) {
    try {
      const optOutState = await fetchOptOutStatus(email);

      Cookies.set(OPT_OUT_COOKIE_NAME, optOutState.optedOut ? "true" : "false");
      applyOptOut();
    } catch (err) {
      captureException(err);
    }
  } else {
    if (!cookie && !email) {
      // We need to set this to show that we've evaluated the opt out status
      // Cookies will only load when cookie is explicitly false
      Cookies.set(OPT_OUT_COOKIE_NAME, "false");
    }
    applyOptOut();
  }
}

export function fetchOptOutStatus(email: string) {
  const formattedEmail = email.trim().toLowerCase();

  return send<OptOutStatus>(
    "GET",
    `/userdata/api/v4/user/ccpa/optOut/`,
    undefined,
    { params: new URLSearchParams({ email: formattedEmail }) }
  );
}
export function setOptOut(email: string) {
  const formattedEmail = email.trim().toLowerCase();

  Cookies.set(OPT_OUT_COOKIE_NAME, "true");

  // Set the LDU flag for the Facebook pixel.
  // Note that this is not considered compliant but we are
  // retaining as an extra layer of protection.
  applyLDUFlag();

  return send("POST", `/userdata/api/v4/user/ccpa/optOut/`, {
    email: formattedEmail,
  });
}
